import { LoadingButton } from '@mui/lab'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, LabelField, LoadingContent, OrderableTableCell } from 'components'
import { getOhioBuildABoxBundleVehicles } from 'controllers/ohioBuildABoxes'
import { OhioBuildABoxBundleDto, OhioBuildABoxBundleVehicleDto } from 'dtos/ohioBuildABoxes'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { clientSideOrderBy } from 'utils'

interface Props {
  initialValues: OhioBuildABoxBundleDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: OhioBuildABoxBundleVehicleDto[]) => void
  open: boolean
}

export default function ManageVehiclesDialog({ initialValues, isLoading, onClose, onSave, open }: Props) {
  const [vehicles, setVehicles] = useState<OhioBuildABoxBundleVehicleDto[]>([])
  const [isGetting, setIsGetting] = useState<boolean>(false)

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OhioBuildABoxBundleVehicleDto>('exporterCompany')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const onChangeSortByDirection = (propertyName: keyof OhioBuildABoxBundleVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    if (open && initialValues.ohioBuildABoxBundleGuid) {
      setIsGetting(true)
      getOhioBuildABoxBundleVehicles(initialValues.ohioBuildABoxBundleGuid)
        .then(res => setVehicles(res.value))
        .finally(() => setIsGetting(false))
    } else setVehicles([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Dialog fullWidth maxWidth='lg' open={open}>
      <Formik enableReinitialize initialValues={initialValues.vehicles} onSubmit={values => onSave(values)} validateOnBlur validateOnChange>
        {({ values, setValues }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2} alignItems='center'>
                <DialogTitle>Build A Box - Bundle</DialogTitle>

                <Grid item xs={12} container>
                  <Grid item xs={12} sm={4}>
                    <LabelField label='Owner' value={initialValues.ohioTitleOwnerName} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <LabelField label='Count Of Vehicles In This Bundle' value={initialValues.vehicles.length} />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='h6'>
                    Manage Vehicles
                  </Typography>
                </Grid>

                <LoadingContent isLoading={isGetting}>
                  <Grid item xs={12}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox'></TableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='exporterCompany'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Exporter
                            </OrderableTableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='buyerCompany'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Buyer
                            </OrderableTableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='vin'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Last 6 of VIN
                            </OrderableTableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='importerStockNumber'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Importer Stock #
                            </OrderableTableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='year'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='make'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<OhioBuildABoxBundleVehicleDto>
                              keyName='model'
                              onChangeOrder={onChangeSortByDirection}
                              order={order}
                              orderBy={orderBy}
                            >
                              Model
                            </OrderableTableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {vehicles.sort(clientSideOrderBy<OhioBuildABoxBundleVehicleDto>(orderBy, order)).map(vehicle => (
                            <TableRow key={vehicle.vehicleGuid}>
                              <TableCell padding='checkbox'>
                                <Checkbox
                                  checked={values.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                                  onChange={() => {
                                    if (values.some(v => v.vehicleGuid === vehicle.vehicleGuid))
                                      setValues(values.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                    else setValues([...values, vehicle])
                                  }}
                                />
                              </TableCell>

                              <TableCell>{vehicle.exporterCompany}</TableCell>

                              <TableCell>{vehicle.buyerCompany ?? ''}</TableCell>

                              <TableCell>{vehicle.vin.substring(vehicle.vin.length - 6)}</TableCell>

                              <TableCell>{vehicle.importerStockNumber}</TableCell>

                              <TableCell>{vehicle.year}</TableCell>

                              <TableCell>{vehicle.make}</TableCell>

                              <TableCell>{vehicle.model}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </LoadingContent>

                <Grid item xs={12}>
                  <GreenText variant='outlined'>
                    {`**Owner:**

Displays the owner associated with the bundle.

This field reflects the selected Owner Name from the Owner Drop-Down and cannot be changed once vehicles are associated with the bundle.

**Count of Vehicles In This Bundle:**

Displays the total number of vehicles currently associated with the bundle.

The count is updated automatically after saving, if additional vehicles are selected in the dialog and successfully added to the bundle.

A maximum of 20 vehicles can be added to a bundle.

If a user attempts to save and the selected vehicles would result in the bundle exceeding 20 vehicles:

   - A warning message is displayed to the user.
   - The system does not save the changes.

**Manage Vehicles:**

Allows users to add vehicles to the bundle using the Add Vehicles Dialog.

Lists all vehicles that meet the following criteria:

   - The Owner matches the owner associated with the bundle.
   - Title Application State = Ohio.
   - The vehicle is not already associated with any “Build A Box.”
   - The Ready For Build A Box date/time is populated under Ohio Titles.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes`}
                  </GreenText>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                CANCEL
              </Button>

              <LoadingButton loading={isLoading} type='submit' variant='contained'>
                SAVE
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
